// Fetch wrapper for the Embeddable Legal/Consent Widget admin API.
//
// This is a SECOND backend (different origin: legal.looprtb.com) from the
// main admin-api wrapped by lib/api.ts. It is guarded by the SAME Cognito
// user pool as admin-web, so a logged-in admin's id_token works verbatim —
// we reuse the very same token-getter (getIdTokenFromPool(getPool())) and
// the ApiError pattern; only the base origin differs.
//
// Reference: PRPs/embeddable-legal-widget-platform.md (admin REST contract).
//
// Kept intentionally tiny — raw fetch, no react-query, exactly like lib/api.ts.
// Pages call it from a useEffect + useState so tests can `vi.mock` it cleanly.
//
// Types are mirrored LOCALLY (a minimal interface set) rather than imported
// from @loop-rtb/legal-widget so admin-web takes no cross-package path/alias
// or runtime dependency on the legal-widget app.

import {
  CognitoUserPool,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

import { getPool } from "./auth";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function getLegalApiBase(): string {
  const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const base = env["VITE_LEGAL_API_BASE"];
  if (!base) {
    throw new Error("VITE_LEGAL_API_BASE not configured");
  }
  return base.replace(/\/+$/, "");
}

/**
 * Public origin of the widget service — the host that serves the embeddable
 * `/w/<niche>/<surface>.js` bundles and the legal pages. Same as the legal API
 * base (the Lambda serves both). Used to build the copy-paste embed snippets.
 */
export function widgetHost(): string {
  return getLegalApiBase();
}

async function getIdTokenFromPool(pool: CognitoUserPool): Promise<string | null> {
  const cu = pool.getCurrentUser();
  if (!cu) return null;
  return await new Promise<string | null>((resolve) => {
    cu.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
      } else {
        resolve(session.getIdToken().getJwtToken());
      }
    });
  });
}

interface LegalApiOptions extends Omit<RequestInit, "body"> {
  /** Optional JSON body — will be stringified and the content type set. */
  body?: unknown;
  /** Override id token (for tests). */
  idToken?: string | null;
}

async function legalRequest<T>(
  path: string,
  opts: LegalApiOptions = {},
): Promise<T> {
  const base = getLegalApiBase();
  const url = path.startsWith("http") ? path : `${base}${path}`;

  const headers = new Headers(opts.headers ?? {});
  if (opts.body !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  headers.set("accept", "application/json");

  let token = opts.idToken ?? null;
  if (token === null) {
    try {
      token = await getIdTokenFromPool(getPool());
    } catch {
      token = null;
    }
  }
  if (token) {
    headers.set("authorization", `Bearer ${token}`);
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
    // Cross-origin call to legal.looprtb.com; the JWT travels in the
    // Authorization header (not a cookie), so we omit credentials exactly
    // like lib/api.ts does.
    credentials: opts.credentials ?? "omit",
  };
  if (opts.body !== undefined) {
    init.body =
      typeof opts.body === "string" ? opts.body : JSON.stringify(opts.body);
  }

  const res = await fetch(url, init);

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const body = parsed as
      | { error?: string; message?: string; detail?: string }
      | null;
    const lead = body?.error ?? body?.message ?? res.statusText;
    const msg = body?.detail ? `${lead}: ${body.detail}` : lead;
    throw new ApiError(res.status, parsed, msg);
  }

  return parsed as T;
}

// ---------------------------------------------------------------------------
// Local type mirrors — a minimal subset of the canonical shapes in
// apps/legal-widget/src/lib/types/legal.types.ts. Mirrored here (not imported)
// to avoid a cross-package path/alias or runtime dependency. Keep in sync with
// the server contract if the legal types change.
// ---------------------------------------------------------------------------

/** The 5 allowlisted render tokens (mirrors RENDER_TOKENS). */
export const RENDER_TOKENS = [
  "siteName",
  "supportEmail",
  "apex",
  "niche",
  "year",
] as const;

/** Exact TrustedForm `data-tf-element-role` values ("none" => no tag). */
export const CONSENT_ROLES = [
  "consent-language",
  "contact-method",
  "consent-grantor-waived-dnc",
  "consent-grantor-waived-purchase-condition",
  "consent-grantor-waived-regulated-technologies",
  "none",
] as const;
export type ConsentItemRole = (typeof CONSENT_ROLES)[number];

export type LegalPageKind =
  | "terms"
  | "privacy"
  | "affiliates"
  | "privacy_request";

export interface LegalSection {
  id: string;
  level: 2 | 3;
  heading: string;
  bodyHtml: string;
}

export interface LegalPage {
  kind: LegalPageKind;
  title: string;
  lastUpdated: string;
  sections: LegalSection[];
}

export interface PartnerList {
  partners: string[];
}

export interface ConsentItem {
  id: string;
  label: string;
  bodyHtml: string;
  role: ConsentItemRole;
}

export interface ConsentContent {
  introHtml: string;
  items: ConsentItem[];
  disclaimerHtml: string;
  partnersAccordionLabel: string;
}

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterContent {
  links: FooterLink[];
  disclaimerHtml: string;
  copyrightHtml: string;
}

/** A niche = a full, isolated content set. */
export interface Niche {
  slug: string;
  displayName: string;
  defaultBrandName: string;
  defaultSupportEmail: string;
  defaultApex: string;
  consent: ConsentContent;
  footer: FooterContent;
  partners: PartnerList;
  pages: LegalPage[];
  createdAt: string;
  version: number;
}

/** Row shape for the niche-list table (summary, not the full content set). */
export interface NicheSummary {
  slug: string;
  displayName: string;
  version: number;
  partnerCount: number;
  pageCount: number;
}

/** OBSERVABILITY ONLY — passive record of which hosts/IPs serve a niche. */
export interface HostRegistryEntry {
  niche: string;
  host: string;
  /** Full page URL stripped at "?" — the dedupe key (one row per niche+url). */
  url: string;
  ip: string;
  surface: "consent" | "footer" | "page";
  firstSeen: string;
  lastSeen: string;
  hitCount: number;
}

/** Branding/identity overrides accepted when cloning a niche. */
export interface NicheCloneOverrides {
  displayName?: string;
  defaultBrandName?: string;
  defaultSupportEmail?: string;
  defaultApex?: string;
}

export interface CloneNicheRequest {
  fromSlug: string;
  newSlug: string;
  overrides: NicheCloneOverrides;
}

// ---------------------------------------------------------------------------
// Response envelopes (unwrapped at the boundary) + typed API surface.
// Mirrors the 6 admin routes documented in the PRP contract.
// ---------------------------------------------------------------------------

interface ListNichesEnvelope {
  niches: NicheSummary[];
}
interface GetNicheEnvelope {
  niche: Niche;
}
interface ListHostsEnvelope {
  hosts: HostRegistryEntry[];
}

export const legalApi = {
  /** GET /api/v1/admin/niches — list summaries for the niche table. */
  async listNiches(): Promise<NicheSummary[]> {
    const r = await legalRequest<ListNichesEnvelope>("/api/v1/admin/niches");
    return r.niches;
  },

  /** GET /api/v1/admin/niches/:slug — the full content set for one niche. */
  async getNiche(slug: string): Promise<Niche> {
    const r = await legalRequest<GetNicheEnvelope>(
      `/api/v1/admin/niches/${encodeURIComponent(slug)}`,
    );
    return r.niche;
  },

  /** POST /api/v1/admin/niches/clone — clone a source niche to a new slug. */
  async cloneNiche(body: CloneNicheRequest): Promise<Niche> {
    const r = await legalRequest<GetNicheEnvelope>(
      "/api/v1/admin/niches/clone",
      { method: "POST", body },
    );
    return r.niche;
  },

  /**
   * PUT /api/v1/admin/niches/:slug — save edits (no version bump). The server
   * sanitizes all rich-text on write; the SPA sends raw editor HTML and literal
   * `{tokens}`.
   */
  async saveNiche(slug: string, niche: Niche): Promise<Niche> {
    const r = await legalRequest<GetNicheEnvelope>(
      `/api/v1/admin/niches/${encodeURIComponent(slug)}`,
      { method: "PUT", body: { niche } },
    );
    return r.niche;
  },

  /**
   * POST /api/v1/admin/niches/:slug/publish — save + publish atomically. The
   * server sanitizes, persists, and BUMPS `version`.
   */
  async publishNiche(slug: string, niche: Niche): Promise<Niche> {
    const r = await legalRequest<GetNicheEnvelope>(
      `/api/v1/admin/niches/${encodeURIComponent(slug)}/publish`,
      { method: "POST", body: { niche } },
    );
    return r.niche;
  },

  /** GET /api/v1/admin/niches/:slug/hosts — observability rows for this niche. */
  async listHosts(slug: string): Promise<HostRegistryEntry[]> {
    const r = await legalRequest<ListHostsEnvelope>(
      `/api/v1/admin/niches/${encodeURIComponent(slug)}/hosts`,
    );
    return r.hosts;
  },
};

export type LegalApi = typeof legalApi;
